import { Platform } from 'models/platforms/platform';
import { AppData, Registry, Settings } from '@lightningjs/sdk';

import { constants, metadata } from 'aliases';
import App from 'App';
import { ApplicationInstance } from '@lightningjs/sdk/src/Launch';
import { generateUUIDv4 } from 'support/generalUtils';
import {
  UnknownKeyMapping,
  UnknownOneTrustKeyMapping,
} from 'models/platforms/unknownKeyMapping';
import { OneTrustRemoteKeys } from 'types/oneTrust';
import { AbstractMouseDeviceIntegration } from '../AbstractMouseDeviceIntegration';

export class UnknownDeviceIntegration extends AbstractMouseDeviceIntegration {
  protected platform = Platform.UNKNOWN;
  private mouseEnterEvent = this.onMouseEnter.bind(this);

  override getKeyMapping() {
    return UnknownKeyMapping;
  }

  override getOneTrustKeyMapping(): OneTrustRemoteKeys {
    return {
      ...super.getOneTrustKeyMapping(),
      ...UnknownOneTrustKeyMapping,
    };
  }

  getDeviceId(): string {
    return this.deviceId;
  }

  hasFlickerIssue(): boolean {
    return false;
  }

  isLowPerformanceModel(): boolean {
    return false;
  }

  isLowMemoryModel(): boolean {
    return false;
  }

  getAppId(): string {
    return metadata.identifier;
  }

  getSubmittedAppVersion(): string {
    return '';
  }

  async load() {
    // Mock a unique id to get around any ad server frequency caps
    this.deviceInfo.adId = generateUUIDv4();

    if (!this.hoverDisabled()) {
      this.setupMouseHandling();
    }

    return Promise.resolve();
  }

  override onAppActive() {
    // Do nothing
  }

  beforeAppClose(callback: (showPopup: boolean) => void) {
    callback(true);
  }

  closeApp() {
    // Not Supported
  }

  handleDeepLink() {
    // Not Supported
    return false;
  }

  handleNetworkChange(callback: (arg: boolean) => void) {
    Registry.addEventListener(window, 'online', function () {
      callback(true);
    });
    Registry.addEventListener(window, 'offline', function () {
      callback(false);
    });
  }

  setScreenSaver(state: boolean) {
    return;
  }

  getAnnouncerEnabled() {
    return false;
  }

  shouldAnnounceContext(): boolean {
    return true;
  }

  shouldAnnounceSeeking(): boolean {
    return true;
  }

  handleAnnouncerChange(callback: (arg: boolean) => void): void {
    // Not Supported
  }

  handleCcChange(callback: (enabled: boolean) => void): void {
    // Not Supported
  }

  useTextTrackComponent(): boolean {
    return false;
  }

  onAdPodStart(): void {
    // Not Supported
  }

  onAdPodEnd(): void {
    // Not Supported
  }

  onPlayerExit(): void {
    // Not Supported
  }

  override getAdvertisingId(): string | null {
    return null;
  }

  override hoverDisabled() {
    return !Settings.get('app', 'enablePointer', false);
  }

  // Simulates certain magic remote events better on browser
  override setupMouseDetectionHandling() {
    if (this.hoverDisabled()) return;

    document.addEventListener('mouseenter', this.mouseEnterEvent, false);

    document.addEventListener(
      'mouseleave',
      this.onMouseLeave.bind(this),
      false,
    );

    document.addEventListener('mousemove', this.mouseEnterEvent, false);
  }

  private onMouseEnter(event: MouseEvent) {
    if (this.hoverDisabled()) return;

    this.setIsHovering(true);
  }

  private onMouseLeave(event: MouseEvent) {
    if (this.hoverDisabled()) return;

    this.setIsHovering(false);
  }

  private setIsHovering(value: boolean) {
    if (this.hoverDisabled()) return;

    this.cursorVisibilityChange(value);

    if (!this.isHovering) {
      document.addEventListener('mousemove', this.mouseEnterEvent, false);
    } else {
      document.removeEventListener('mousemove', this.mouseEnterEvent, false);
    }
  }

  // Browser keyboard overrides for debugging
  override handleDebugKeyInputs(event: KeyboardEvent, app: App) {
    if (AppData?.isProduction) return;

    const key = event.key;
    // Text to speech
    if (key === 't') {
      app.announcerEnabled = !app.announcerEnabled;
    }

    // Captions
    if (key === 'c') {
      const ccEnabled = !(
        AppData?.storageService.subtitle.get() !== constants.closedCaption.none
      );

      ApplicationInstance?.emit('ccChange', ccEnabled);
    }

    if (key === 'h') {
      this.setIsHovering(!this.isHovering);
    }

    // If the user does a common remote navigation, simulate magic remote being turned off
    const remoteKeys = [
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'Enter',
      'Back',
    ];
    if (this.isHovering && remoteKeys.includes(key)) {
      this.setIsHovering(false);
    }
  }

  override supportsParentalPin(): boolean {
    return true;
  }
}
